import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EllipsisVerticalIcon,
  Link,
  Table,
  TableCell,
  TableRow,
} from '@fukuri-admins/ui/common';
import { iconButtonVariants } from '@fukuri-admins/ui/common/IconButton/logic';
import styles from './styles.module.scss';

/**
 * テーブル列定義（カスタムフィールド列専用）
 *
 * - key: `custom_field_${label}` 形式（日本語可）
 */
type ColumnDef = {
  /** 列キー（`custom_field_${label}` 形式） */
  key: `custom_field_${string}`;
  /** テーブルヘッダーに表示するラベル */
  label: string;
};

/**
 * 利用企業テーブルの行データ型
 *
 * - 固定フィールドのほかに、カスタムフィールド列（`custom_field_${label}` キー）を動的に保持できる
 * - 表示整形済みの値を受け取る（converter で API 型から変換される）
 */
type ClientRow = {
  /** 利用企業の一意な識別子（API の client.id） */
  id: number;
  /** 会社名 */
  name: string;
  /**
   * グループ名
   *
   * 最初のグループ名を表示。未設定の場合は '-'
   */
  groupName: string;
  /**
   * 取引店舗名
   *
   * 未設定の場合は '-'
   */
  partnerBranchName: string;
  /**
   * 担当者名
   *
   * 未設定の場合は '-'
   */
  contactPersonName: string;
  /**
   * 利用予定者数（表示整形済み。client.employee_count が null/undefined の場合は '-'）
   */
  employeeCount: string;
  /**
   * 利用者数（表示整形済み。client.user_count が null/undefined の場合は '-'）
   *
   * ヘッダーに「家族アカウントも含みます。」ヘルプツールチップを表示する
   */
  userCountDisplay: string;
  /**
   * 課金対象者数（表示整形済み。client.non_invited_user_count が null/undefined の場合は '-'）
   */
  nonInvitedUserCount: string;
  /**
   * 契約プラン名
   *
   * 有効な契約がない場合は '-'
   */
  contractPlanName: string;
  /**
   * 契約プラン詳細画面への遷移パス
   *
   * 有効な契約がない場合は空文字（Presentation 側でリンク非表示の判定に使う）
   */
  contractPlanLink: string;
  /** 作成日時（表示整形済み） */
  createdAt: string;
  /** 更新日時（表示整形済み） */
  updatedAt: string;
  /**
   * 与信状態
   *
   * true: 与信完了、false: 未完了、undefined: 与信機能無効（非表示）
   */
  isCreditGranted?: boolean;
  /**
   * 利用者数（削除ダイアログの表示情報として使用）
   *
   * API の user_count（optional）に fallback 0 を適用した値
   */
  userCount: number;
  /**
   * カスタムフィールドの値（動的）
   *
   * - キーは `custom_field_${label}` 形式（日本語可）
   * - 当該カスタムフィールド定義に対応する値が無い行ではキーが含まれず undefined になる
   * - `extractCustomFields` / `formatCustomFieldValues` の戻り値型は `Record<string, string>` のため、値は常に文字列
   * - NOTE: optional index signature 構文（`[key]?:`）は biome 未対応のため `| undefined` で代替
   */
  [key: `custom_field_${string}`]: string | undefined;
};

/** Table のヘッダーが受け取るヘルプオブジェクトの型（content はこのファイルで組み立てる） */
type HeaderHelpContent = NonNullable<
  React.ComponentProps<typeof Table>['headers'][number]['helpContent']
>;

type Props = {
  /** テーブルに表示する利用企業行データ */
  rows: ClientRow[];
  /**
   * カスタムフィールド列定義（optional）
   *
   * 定義がある場合はグループ列と取引店舗列の間に挿入される
   */
  customFieldColumns?: ColumnDef[];
  /**
   * 与信評価機能フラグ
   *
   * true のとき与信状況列を先頭に追加する
   */
  creditEvaluationEnabled?: boolean;
  /**
   * マスタ取引店舗一覧（GET /api/v1/{threeLetterCode}/partner_admins/partner_branches）に
   * 1件以上の取引店舗が存在するか（PEN-985）
   *
   * true のときのみ取引店舗列を表示する。パートナー配下に取引店舗が1件も無い場合は、
   * 実データが無い列を表示しても意味がないため非表示にする（PEN-989）。
   */
  hasPartnerBranch: boolean;
  /**
   * 行の「編集」押下時のコールバック
   *
   * @param clientId 操作対象の利用企業 ID
   */
  onEditClient: (clientId: number) => void;
  /**
   * 行の「削除」押下時のコールバック
   *
   * @param row 操作対象の利用企業行データ（削除ダイアログに5項目を渡すため row 全体を渡す）
   */
  onDeleteClient: (row: ClientRow) => void;
  /**
   * 行クリック時のコールバック（利用企業詳細画面への遷移）
   *
   * @param clientId 操作対象の利用企業 ID
   */
  onRowClick: (clientId: number) => void;
  /**
   * 与信確認ボタン押下時のコールバック
   *
   * creditEvaluationEnabled=true かつ与信未完了の行に表示される「確認」ボタン押下時に呼ばれる
   *
   * @param clientId 操作対象の利用企業 ID
   */
  onGrantCredit?: (clientId: number) => void;
  /** 「利用者数」ヘルプアイコンのツールチップ表示ハンドラー */
  showTooltip: HeaderHelpContent['showHelp'];
  /** ツールチップを非表示にするハンドラー */
  hideTooltip: HeaderHelpContent['hideHelp'];
  /** ツールチップの id */
  tooltipId: HeaderHelpContent['helpId'];
};

/**
 * 利用企業一覧テーブル（テーブル本体のみ）
 *
 * - 固定列（左）: [与信状況（creditEvaluationEnabled=true 時のみ）] / 会社名 / グループ
 * - 動的列: customFieldColumns が渡された場合はグループと取引店舗列の間に挿入される
 * - 固定列（右）: [取引店舗（hasPartnerBranch 時のみ）] / 担当者 /
 *   利用予定者数 / 利用者数 / 課金対象者数 / 契約プラン / 作成日時 / 更新日時 / 操作
 * - 取引店舗列: hasPartnerBranch（マスタ取引店舗一覧に1件以上存在するか）が true のときのみ全環境で表示する（PEN-985 / PEN-989）
 * - 利用者数列: ヘッダーに「家族アカウントも含みます。」ヘルプツールチップを表示する（MMT-219）
 * - 契約プラン列: contractPlanLink がある場合は契約プラン詳細画面へのリンク、無い場合は '-' テキスト（LEA-3497）
 * - 与信状況列: 与信済みは「完了済み」テキスト、未完了は「確認」ボタンを表示して onGrantCredit を呼ぶ
 * - 未与信行: isCreditGranted=false の行に creditIncompleteRow クラスを付与してハイライトする
 * - 設定アクション: 行内の DropdownMenu 経由で編集・削除コールバックを呼び出す
 * - ページャーは親の ClientListPresentation が担う
 */
export const ClientsTable: React.FC<Props> = ({
  rows,
  customFieldColumns,
  creditEvaluationEnabled,
  hasPartnerBranch,
  onEditClient,
  onDeleteClient,
  onRowClick,
  onGrantCredit,
  showTooltip,
  hideTooltip,
  tooltipId,
}) => (
  <Table
    scrollable
    headRowClassName={styles.headRow}
    headers={[
      ...(creditEvaluationEnabled
        ? [{ label: '与信状況', className: styles.creditStatusCol }]
        : []),
      { label: '会社名', className: styles.companyNameCol },
      { label: 'グループ', className: styles.groupCol },
      ...(customFieldColumns?.map(col => ({ label: col.label, key: col.key })) ?? []),
      ...(hasPartnerBranch ? [{ label: '取引店舗', className: styles.partnerBranchCol }] : []),
      { label: '担当者', className: styles.contactPersonCol },
      { label: '利用予定者数', className: styles.employeeCountCol },
      {
        label: '利用者数',
        className: styles.userCountCol,
        helpContent: {
          content: '家族アカウントも含みます。',
          showHelp: showTooltip,
          hideHelp: hideTooltip,
          helpId: tooltipId,
        },
      },
      { label: '課金対象者数', className: styles.nonInvitedUserCountCol },
      { label: '契約プラン', className: styles.contractPlanCol },
      { label: '作成日時', className: styles.createdAtCol },
      { label: '更新日時', className: styles.updatedAtCol },
      { label: '操作', className: styles.actionCol },
    ]}
  >
    {rows.map(row => (
      <TableRow
        key={row.id}
        className={
          creditEvaluationEnabled && row.isCreditGranted === false
            ? styles.creditIncompleteRow
            : undefined
        }
        onClick={() => onRowClick(row.id)}
      >
        {creditEvaluationEnabled && (
          <TableCell className={styles.creditStatusCol}>
            {/* TODO: デザイン見直し予定。現状は旧実装の ActionButton を踏襲した暫定 UI */}
            {row.isCreditGranted ? (
              <span className={styles.creditStatusGranted}>完了済み</span>
            ) : (
              <button
                type='button'
                className={styles.creditStatusButton}
                onClick={e => {
                  e.stopPropagation();
                  onGrantCredit?.(row.id);
                }}
              >
                確認
              </button>
            )}
          </TableCell>
        )}
        <TableCell className={styles.companyNameCol}>{row.name}</TableCell>
        <TableCell className={styles.groupCol}>{row.groupName}</TableCell>
        {customFieldColumns?.map(col => (
          <TableCell key={col.key}>{row[col.key] ?? '-'}</TableCell>
        ))}
        {hasPartnerBranch && (
          <TableCell className={styles.partnerBranchCol}>{row.partnerBranchName}</TableCell>
        )}
        <TableCell className={styles.contactPersonCol}>{row.contactPersonName}</TableCell>
        <TableCell className={styles.employeeCountCol}>{row.employeeCount}</TableCell>
        <TableCell className={styles.userCountCol}>{row.userCountDisplay}</TableCell>
        <TableCell className={styles.nonInvitedUserCountCol}>{row.nonInvitedUserCount}</TableCell>
        <TableCell className={styles.contractPlanCol}>
          {row.contractPlanLink ? (
            <Link
              to={row.contractPlanLink}
              isExternal
              target='_blank'
              onClick={e => e.stopPropagation()}
            >
              {row.contractPlanName}
            </Link>
          ) : (
            row.contractPlanName
          )}
        </TableCell>
        <TableCell className={styles.createdAtCol}>{row.createdAt}</TableCell>
        <TableCell className={styles.updatedAtCol}>{row.updatedAt}</TableCell>
        <TableCell className={styles.actionCol} align='right'>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <button
                type='button'
                className={iconButtonVariants({ variant: 'ghost', size: 'sm' })}
                aria-label='操作メニューを開く'
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVerticalIcon />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' onClick={e => e.stopPropagation()}>
              <DropdownMenuItem onSelect={() => onEditClient(row.id)}>編集</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className={styles.deleteItem} onSelect={() => onDeleteClient(row)}>
                削除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ))}
  </Table>
);
